/**
 * 订单表单弹窗主组件：持有全部表单状态、字段校验与提交流程，按区块拆分渲染各 Section
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { get, post, put } from '../../../../api/client';
import { createIdempotencyKey } from '../../../../lib/idempotency';
import { useAuthStore } from '../../../../stores/authStore';
import { useOrderBehavior } from '../../../../hooks/useOrderBehavior';
import { createOrderFormSchema, OrderFormData } from '../OrderFormSchema';
import type { OrderItem, OrderItemDetail, ServiceType, OrderCategory } from '../../../../types';
import { handleApiError, showErrorNotification, isWriteConflict, getSlotDuplicateHint, type SlotDuplicateHintItem, type SlotPreviewResult } from '../../../../utils/errorHandler';
import { bookingDays, todayString } from '../../../../utils/dateUtils';
import type { PartyOption, Props } from './types';
import { calcDuration, emptyItem, initForm,
  arrivalMaxOf, arrivalErrorOf, endDateErrorOf, endTimeErrorOf, itemRowsError, paidAmountError, ORDER_ITEMS_MAX } from './utils';
import { hasNoPaymentObligation, paymentStatusOf, pendingBalanceOf, roundMoney, itemsDailySubtotal } from '../../../../utils/paymentAmount';
import { BasicInfoSection } from './sections/BasicInfoSection';
import { TimeSection } from './sections/TimeSection';
import { CustomerSection } from './sections/CustomerSection';
import { ActivitySection } from './sections/ActivitySection';
import { ItemsSection } from './sections/ItemsSection';
import { AmountSection } from './sections/AmountSection';
import { NotesSection } from './sections/NotesSection';
import SlotDuplicateDialog from '../slot-duplicate-dialog';

export default function OrderFormModal({ mode, initial, onClose, onSaved, onConflict }: Props) {
  const { profile } = useAuthStore();
  const [orderItems, setOrderItems] = useState<OrderItemDetail[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [categories, setCategories] = useState<OrderCategory[]>([]);
  const [customers, setCustomers] = useState<PartyOption[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDrop, setShowCustomerDrop] = useState(false);
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);
  const [paymentVoucherUrl, setPaymentVoucherUrl] = useState<string>(() => initial?.paymentVoucherUrl ?? '');
  const [auditNotice, setAuditNotice] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  // 明细加载失败标记：PUT 是整体替换，读不回来就等于「这张单没有明细」，
  // 放任保存会把真实明细清空，所以必须挡住提交
  const [itemsLoadError, setItemsLoadError] = useState<string | null>(null);
  // 明细闸门（条数上限 + 逐行校验）的结果：显示在明细区块内「添加明细」下方，与两端同位；
  // 改一行就撤掉，不留着吓人
  const [itemsError, setItemsError] = useState('');
  useEffect(() => { setItemsError(''); }, [orderItems]);
  const scrollRef = useRef<HTMLDivElement>(null);

  // 补录模式：控制预约日期是否允许选择过去（状态在服务端，四端同源）
  const { behavior: orderBehavior, refresh: refreshOrderBehavior } = useOrderBehavior();
  const allowPastDates = orderBehavior.allowPastDates;
  // 是否存在有效明细（至少一行填写了名称）：既决定订单金额是否由合计接管，也决定它参不参与校验
  const hasValidItems = useMemo(() => orderItems.some(it => it.itemName.trim() !== ''), [orderItems]);
  // 编辑的那张单**库里就没有金额**（历史遗留的 0 元单）。这一格的报错要说清「补填才能保存」，
  // 而不是只对他说「必须大于 0」——他并没有填进一个 0，是这张单本来就缺金额。
  // 服务端同一句是兜底：三端 PUT 总上送这一格，越过前端闸门也只会收到「订单金额必须大于 0」，
  // 所以真正让用户看到分流文案的位置就在表单这里。
  const amountMissingOnEdit = mode === 'edit' && !(Number(initial?.orderAmount) > 0);
  const formSchema = useMemo(
    () => createOrderFormSchema(
      allowPastDates, hasValidItems, orderBehavior.lockedByAdmin, mode === 'edit', amountMissingOnEdit
    ),
    [allowPastDates, hasValidItems, orderBehavior.lockedByAdmin, mode, amountMissingOnEdit]
  );
  const orderStartMin = useMemo(() => {
    if (allowPastDates) return undefined;
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, [allowPastDates]);

  const { control, handleSubmit, formState: { errors }, setValue, watch, getValues, setError, clearErrors } = useForm<OrderFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: initForm(initial)
  });

  const form = watch();

  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 280);
  };

  const selectedCustomer = customers.find(c => c.id === form.customerId) ?? null;

  const durationHint = useMemo(
    () => calcDuration(form.orderStartDate, form.orderEndDate, form.isFullDay, form.orderTimeStart, form.orderTimeEnd),
    [form.orderStartDate, form.orderEndDate, form.isFullDay, form.orderTimeStart, form.orderTimeEnd]
  );

  // 预计到达时间最大值（规则本体在 utils.arrivalMaxOf，与 hmos/Flutter 规则层同源）
  const arrivalMaxDateTime = useMemo(
    () => arrivalMaxOf(form.orderStartDate, form.isFullDay, form.orderTimeStart),
    [form.orderStartDate, form.isFullDay, form.orderTimeStart]
  );

  // 校验预计到达时间并返回错误信息
  const getArrivalError = useCallback((val: string): string | undefined =>
    arrivalErrorOf(val, form.orderStartDate, form.isFullDay, form.orderTimeStart),
  [form.orderStartDate, form.isFullDay, form.orderTimeStart]);

  // 预计到达时间变更处理：非法值当场判错，清空动作统一交给下面的 effect
  const handleArrivalDateTimeChange = useCallback((val: string) => {
    setValue('arrivalDateTime', val);
    const err = getArrivalError(val);
    if (err) setError('arrivalDateTime', { message: err });
    else clearErrors('arrivalDateTime');
  }, [getArrivalError, setValue, setError, clearErrors]);

  /**
   * 预约日期/全天/开始时间改变后重算已填的到达时间：非法就清空并提示，
   * 与两端移动表单同一条交互（红字在桌面端够显眼，手机上看不见，清空才是统一的纠偏）。
   * 首帧不清空：编辑模式回填的历史值可能本就已非法，一打开就抹字段比留红字更扰民。
   */
  const arrivalCheckedOnce = useRef(false);
  useEffect(() => {
    if (!form.arrivalDateTime) {
      arrivalCheckedOnce.current = true;
      return;
    }
    const err = getArrivalError(form.arrivalDateTime);
    const first = !arrivalCheckedOnce.current;
    arrivalCheckedOnce.current = true;
    if (!err) {
      clearErrors('arrivalDateTime');
      return;
    }
    setError('arrivalDateTime', { message: err });
    if (first) return;
    setValue('arrivalDateTime', '');
    showErrorNotification(`${err}，已清空请重新选择`);
  }, [form.orderStartDate, form.isFullDay, form.orderTimeStart, getArrivalError,
      form.arrivalDateTime, setError, clearErrors, setValue]);

  // 预约天数（多天活动时用于乘以明细单价）—— 唯一实现 bookingDays，勿在此重算。
  // null = 用户还没选预约日期，合计暂时按单日预览，提交前有必填校验挡着。
  const numDays = useMemo(
    () => bookingDays(form.orderStartDate, form.orderEndDate),
    [form.orderStartDate, form.orderEndDate],
  );

  // 订单明细每日小计：web 端唯一实现（逐行夹两位再求和、末尾再夹一次），
  // 浮点乘法的长尾数不能让服务端的两位小数闸门来挡
  const itemsSubtotalPerDay = useMemo(() => itemsDailySubtotal(orderItems), [orderItems]);

  // 订单明细合计（× 天数）
  const itemsTotal = useMemo(
    () => roundMoney(itemsSubtotalPerDay * (numDays ?? 1)),
    [itemsSubtotalPerDay, numDays],
  );

  // 实际订单金额：有明细时由合计自动决定，否则用手填值
  const effectiveOrderAmount = useMemo(() => {
    return hasValidItems ? String(itemsTotal) : form.orderAmount;
  }, [hasValidItems, itemsTotal, form.orderAmount]);

  // 付款状态相关计算
  const autoPaymentStatus = useMemo(() => {
    return paymentStatusOf(effectiveOrderAmount, form.paidAmount);
  }, [effectiveOrderAmount, form.paidAmount]);

  const isPaid = useMemo(() => {
    return autoPaymentStatus !== '未付款';
  }, [autoPaymentStatus]);

  const remainBalance = useMemo(() => {
    return pendingBalanceOf(Number(effectiveOrderAmount || 0), Number(form.paidAmount || 0));
  }, [effectiveOrderAmount, form.paidAmount]);

  const voucherLabel = useMemo(() => {
    return autoPaymentStatus === '已结清' ? '结清凭证' : '部分收款凭证';
  }, [autoPaymentStatus]);

  /**
   * 表单里那枚付款状态 chip：金额仍为 0 的**历史遗留单**整块不下发（新单已被金额闸门禁止，
   * 这一档只给旧数据兜底），判据与列表卡、详情徽章同一条，
   * 所以这里只有后端那三个枚举档，不再养「无金额」这第四档文案。
   * autoPaymentStatus 本身不动——isPaid / voucherLabel 都靠跟它比较。
   */
  const statusStyle = useMemo(() => {
    if (hasNoPaymentObligation(effectiveOrderAmount, form.paidAmount)) return null;
    const paymentStatusConfig: Record<string, { bg: string; text: string; icon: string; label: string }> = {
      '未付款':   { bg: 'bg-amber-50 border-amber-200',   text: 'text-amber-600',   icon: 'ri-time-line',           label: '未付款' },
      '部分付款': { bg: 'bg-sky-50 border-sky-200',       text: 'text-sky-600',     icon: 'ri-wallet-3-line',       label: '部分付款' },
      '已结清':   { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-600', icon: 'ri-checkbox-circle-line', label: '已结清' },
    };
    return paymentStatusConfig[autoPaymentStatus] ?? paymentStatusConfig['未付款'];
  }, [autoPaymentStatus, effectiveOrderAmount, form.paidAmount]);

  // 订单抵消保护：已通过抵消结算的订单，付款信息不可编辑
  const isNettedOrder = useMemo(() => {
    return !!(initial?.paymentMethod && initial.paymentMethod.includes('订单抵消'));
  }, [initial?.paymentMethod]);

  /**
   * 已付金额从 0 变成有值时把付款日期顺手落成今天（裁定 2026-10-02，两端移动表单同一条闸门）：
   * 这一跳变说明用户正在记一笔刚收到的钱，再让他点一次日期选择器是白跑腿。
   * 刻意只认跳变、不在挂载时补：编辑一张「已付>0 而日期为空」的历史单，一打开就把日期改成今天，
   * 等于被动改写别人的历史记录。
   */
  const prevIsPaidRef = useRef<boolean | null>(null);
  useEffect(() => {
    const wasPaid = prevIsPaidRef.current;
    prevIsPaidRef.current = isPaid;
    if (wasPaid === null || wasPaid || !isPaid) return;
    if (!getValues('paymentDate')) setValue('paymentDate', todayString());
  }, [isPaid, getValues, setValue]);

  // 订单明细操作
  const addItem = () => {
    // 与两端移动表单同一句：第 101 条会被后端 @ArrayMaxSize 挡成 400，就地提示比让用户撞一次强
    if (orderItems.length >= ORDER_ITEMS_MAX) {
      setItemsError(`单笔订单明细不能超过 ${ORDER_ITEMS_MAX} 条`);
      return;
    }
    setOrderItems(prev => [...prev, emptyItem()]);
  };
  const removeItem = (idx: number) => setOrderItems(prev => prev.filter((_, i) => i !== idx));
  const setItem = (idx: number, field: keyof OrderItemDetail, val: string) => {
    setOrderItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const newVal = (field === 'quantity' || field === 'unitPrice') ? Number(val) : val;
      return { ...it, [field]: newVal };
    }));
  };

  useEffect(() => {
    get<ServiceType[]>('/service-types').then(data => {
      if (data) setServiceTypes(data);
    }).catch(error => {
      console.error('获取服务类型失败:', error);
    });

    get<OrderCategory[]>('/order-categories').then(data => {
      if (data) setCategories(data);
    }).catch(error => {
      console.error('获取订单分类失败:', error);
    });

    // 加载当前用户的客户列表（后端 GET /customers 已按 UserCustomer 绑定过滤，无需再拉 user-customers）
    // 注：之前调用的 /user-customers 路由未在后端暴露（仅 admin 模块有 /admin/users/:id/customers），
    // 导致客户列表永远为空 → 搜索恒显示“未找到匹配客户”。
    if (profile?.id) {
      get<PartyOption[]>('/customers').then(data => {
        if (!data) return;
        // 过滤掉当前用户自身（避免将自己选为“客户”）
        setCustomers(data.filter(c => c.id !== profile.id));
      }).catch(error => {
        console.error('获取客户列表失败:', error);
      });
    }

    // 编辑模式下加载订单明细
    if (mode === 'edit' && initial?.id) {
      setItemsLoadError(null);
      get<OrderItemDetail[]>(`/orders/${initial.id}/items`).then(data => {
        // 空数组是真的没有明细，必须照样落下去；请求失败时拿不到 data，
        // 这时候不能留着 [] 当「没有明细」——保存会把整张单的明细清空
        if (!data) {
          setItemsLoadError('订单明细加载失败，请关闭弹窗重试');
          return;
        }
        setOrderItems(data.map(d => ({
          id: d.id,
          itemName: d.itemName ?? '',
          itemSpec: d.itemSpec ?? null,
          quantity: Number(d.quantity ?? 1),
          unitPrice: Number(d.unitPrice ?? 0),
        })));
      }).catch(error => {
        console.error('获取订单明细失败:', error);
        setItemsLoadError('订单明细加载失败，请关闭弹窗重试');
      });
    }
  }, [mode, initial?.id, profile?.id]);

  const filteredCustomers = useMemo(() => {
    const kw = customerSearch.toLowerCase();
    return customers.filter(c => {
      return c.fullName?.toLowerCase().includes(kw) || c.companyName?.toLowerCase().includes(kw) || c.phone?.includes(kw);
    });
  }, [customers, customerSearch]);

  // 结束日期变更：校验不能早于预约日期（规则本体在 utils，与 schema 提交时同一条）
  const handleEndDateChange = (val: string) => {
    setValue('orderEndDate', val);
    const err = endDateErrorOf(form.orderStartDate, val);
    if (err) setError('orderEndDate', { message: err });
    else clearErrors('orderEndDate');
  };

  // 结束时间变更：同一天时不能早于开始时间
  const handleEndTimeChange = (val: string) => {
    setValue('orderTimeEnd', val);
    const err = endTimeErrorOf(
      form.orderStartDate, form.orderEndDate, form.isFullDay, form.orderTimeStart, val);
    if (err) setError('orderTimeEnd', { message: err });
    else clearErrors('orderTimeEnd');
  };

  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * 幂等键：一次弹窗会话一把，首次点击提交时才生成，之后重试沿用同一把。
   * 挡的是「点了提交、响应丢在半路、用户再点一次」——后端认得这把键，会回首次结果，
   * 而不是安静地多落一张单。校验失败的那次没建单，键没入库，改完再提交不受影响。
   */
  const idempotencyKeyRef = useRef('');
  /**
   * 疑似重复订单：后端只提示、由人裁定。命中时表单原样留着，弹确认框列出先提交那几张单。
   * 判重的两个输入（客户、预约日期）一改，这份结果就作废——不能拿旧比对去让用户确认新内容。
   */
  const [slotDuplicate, setSlotDuplicate] = useState<{ message: string; duplicates: SlotDuplicateHintItem[] } | null>(null);
  // 「不是同一订单」只授权紧接的那一次重发，不是一次性开关
  const confirmedDuplicateRef = useRef(false);
  useEffect(() => {
    setSlotDuplicate(null);
    confirmedDuplicateRef.current = false;
    // 只跟着预约日期复位：判据里没有客户（同日即命中），换客户不该把已经摊出来的提示抹掉
  }, [form.orderStartDate]);

  /**
   * 填完预约日期就即时预告当日档期：只读、**不阻断**，判据全在服务端
   * （`GET /orders/slot-preview` 与提交闸门读同一段代码，不会各说一套）。
   *
   * **一问一答只看日子**：`orderDate` 一填就问，客户填没填、填的是谁都不参与（一天只到得了场，
   * 那天已经挂着单就该说一声）。所以这里的键里也没有客户——换客户不重新发问。
   *
   * 只有收入向才问——`includes('支出')` 与后端 `isExpenseCategory` 同式，支出订单同日
   * 采买几笔都不构成我要到场的冲突。命中只在日期下方挂一条横幅，提交按钮照旧可点，
   * 真拦人的还是提交时那张确认框；预览请求失败也只是横幅收起，绝不把「没问到」演成「没有冲突」。
   */
  const [slotPreview, setSlotPreview] = useState<SlotPreviewResult | null>(null);
  const slotPreviewKey = useMemo(() => {
    if (!form.orderStartDate) return '';
    if ((form.orderCategory ?? '').includes('支出')) return '';
    return [
      form.orderStartDate,
      // 全天＝占满当天；只有真填了钟点才把时间段递上去，与提交时 `!isFullDay && 值` 同一条取值
      form.isFullDay ? 'full' : `${form.orderTimeStart ?? ''}~${form.orderTimeEnd ?? ''}`,
      mode === 'edit' ? initial?.id ?? '' : '',
    ].join('|');
  }, [form.orderStartDate, form.orderCategory,
      form.isFullDay, form.orderTimeStart, form.orderTimeEnd, mode, initial?.id]);

  const slotPreviewParams = useMemo<Record<string, unknown> | null>(() => {
    if (!slotPreviewKey) return null;
    const params: Record<string, unknown> = {
      orderDate: form.orderStartDate,
      orderCategory: form.orderCategory,
    };
    if (!form.isFullDay) {
      if (form.orderTimeStart) params.orderTimeStart = form.orderTimeStart;
      if (form.orderTimeEnd) params.orderTimeEnd = form.orderTimeEnd;
    }
    // 编辑模式必须把自己摘出去，否则预告条永远点名手上正在改的这张单
    if (mode === 'edit' && initial?.id) params.excludeOrderId = initial.id;
    return params;
  }, [slotPreviewKey, form.orderStartDate, form.orderCategory,
      form.isFullDay, form.orderTimeStart, form.orderTimeEnd, mode, initial?.id]);

  // 判重输入一改就重问；400ms 防抖挡住「一路敲日期」的中间态，又不至于让人等
  useEffect(() => {
    if (!slotPreviewParams) {
      setSlotPreview(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      get<SlotPreviewResult>('/orders/slot-preview', { params: slotPreviewParams })
        .then((data) => {
          if (cancelled) return;
          setSlotPreview(data?.hasConflict === true ? data : null);
        })
        .catch((error) => {
          if (cancelled) return;
          console.error('档期即时预告查询失败:', error);
          setSlotPreview(null);
        });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [slotPreviewParams]);
  // 编辑乐观锁令牌：手上这份快照的 updatedAt。撞 409 后换成服务端当前值，「刷新后重新提交」才真能提交出去
  const [lockToken, setLockToken] = useState<string | null>(initial?.updatedAt ?? null);
  useEffect(() => { setLockToken(initial?.updatedAt ?? null); }, [initial?.updatedAt]);

  const onSubmit = async (data: OrderFormData) => {
    // 明细没读回来就保存 = 用空数组替换整张单的明细，这一条必须挡在最前面
    if (itemsLoadError) {
      setSubmitError(itemsLoadError);
      return;
    }
    // 明细逐行闸门：无名行此前会被下面的 filter 静默丢掉（以为存了 3 条、实际 2 条），
    // 1.999 这类三位小数此前能一路发到后端被 Decimal(10,2) 四舍五入成 2.00
    const rowErr = itemRowsError(orderItems);
    if (rowErr) {
      setItemsError(rowErr);
      return;
    }
    // 超收闸门：实收不得超过订单金额。服务端 `assertPaidWithinOrderAmount` 同一句、同一判据，
    // 放这里是让用户先看见——越过它就只剩一个 400 红条，猜不出是哪一格的问题。
    // 有明细时金额这一格是锁死的只读回显，出路只能落在明细或收款记录上，句子随之分流。
    const overPayErr = paidAmountError(effectiveOrderAmount, data.paidAmount, hasValidItems);
    if (overPayErr) {
      setError('paidAmount', { message: overPayErr });
      showErrorNotification(overPayErr);
      return;
    }
    setSaving(true);
    setSubmitError(null);
    try {
      // 订单明细：随订单主体一次提交，后端事务原子写入（避免两次 HTTP 产生无明细的半成品订单）
      // - 创建模式：有效明细才带上 items（空数组无必要）
      // - 编辑模式：不论是否有效都传 items，允许后端以空数组语义清空旧明细
      const validItems = orderItems
        .filter(it => it.itemName.trim())
        .map(it => ({
          itemName: it.itemName.trim(),
          itemSpec: it.itemSpec?.trim() || null,
          // 数量 0 是合法值（这一行就算 0）；「赠送项」挂在**单价为 0** 上，不挂在数量上，
          // 整单合计必须 > 0 由 itemRowsError 末尾那条统一挡。
          // `|| 1` 会把填了 0 悄悄改成 1，与后端 `quantity ?? 1`（只有键未提供才落 1）存的值不一致；
          // 「没填」在上面 itemRowsError 就被 `请填写数量` 挡下了，走到这里的一定是个真数字。
          quantity: Number(it.quantity),
          unitPrice: Number(it.unitPrice) || 0,
        }));

      // 结束日期等于预约日期时按单日处理，存 null
      const effectiveEndDate = (data.orderEndDate && data.orderEndDate !== data.orderStartDate)
        ? data.orderEndDate : null;

      // 订单分类（'收入订单'/'支出订单'或自定义名称）是订单的核心字段，直接传名称字符串给后端
      // 注：use_id/审核状态/支付状态/订单状态/订单编号均由后端权威计算/生成，前端不传；activity_duration 无对应库表列
      const payload: Record<string, unknown> = {
        orderCategory: data.orderCategory,
        serviceType: data.serviceTypeId || null,
        customerId: data.customerId || null,
        contactPerson: data.contactPerson || null,
        contactPhone: data.contactPhone || null,
        activityName: data.activityName || null,
        activityAddress: data.activityAddress || null,
        // 编辑态这一格是只读回显：值即库中现值，服务端同值放行、异值 400（不静默改单）
        orderStartDate: data.orderStartDate,
        orderEndDate: effectiveEndDate,
        isFullDay: data.isFullDay,
        orderTimeStart: !data.isFullDay && data.orderTimeStart ? data.orderTimeStart : null,
        orderTimeEnd: !data.isFullDay && data.orderTimeEnd ? data.orderTimeEnd : null,
        arrivalDateTime: data.arrivalDateTime || null,
        // 两笔金额恒发数字：库里是 NOT NULL Decimal(10,2)，编辑时发 null 会被契约挡成 400，
        // 此前则是直接撞成 500。订单金额这里**不再兜 0**——`|| 0` 等于把「空着的金额」
        // 重新变成一张 0 元单，而它在提交前就该被 orderAmountError / itemRowsError 挡下。
        orderAmount: Number(effectiveOrderAmount),
        paidAmount: Number(data.paidAmount) || 0,
        // 未付款的单这一格界面上已经不给了，就把值一并归零，别把上一次选的渠道留在库里；
        // 抵消单例外——这一列同时是轧差标记，抹掉等于把这张单从对冲结算里拽出来。
        paymentMethod: isPaid || isNettedOrder ? (data.paymentMethod || null) : null,
        paymentDate: isPaid ? (data.paymentDate || null) : null,
        paymentVoucherUrl: isPaid ? (paymentVoucherUrl || null) : null,
        orderSpecialInstructions: data.orderSpecialInstructions || null,
      };

      // 用户判定「不是同一订单」的那一次授权：只随紧接的这次重发上送（创建与编辑共用这一格）
      if (confirmedDuplicateRef.current) payload.confirmSlotDuplicate = true;

      // 创建模式：带明细才提交 items；编辑模式：始终提交 items（允许空数组语义清空）
      if (mode === 'create') {
        if (validItems.length > 0) payload.items = validItems;
        if (!idempotencyKeyRef.current) idempotencyKeyRef.current = createIdempotencyKey();
        await post<{ id: string }>('/orders', payload, {
          headers: { 'Idempotency-Key': idempotencyKeyRef.current },
        });
      } else {
        payload.items = validItems;
        // 后端列为空时令牌是 null：不上送即退回「不做冲突检测」，宁可不挡也不误挡
        if (lockToken) payload.expectedUpdatedAt = lockToken;
        await put(`/orders/${initial!.id}`, payload);
      }

      // 检查客户是否为系统注册用户，若是则发送审核通知
      if (mode === 'create' && data.customerId) {
        const customerIsUser = selectedCustomer?.isUser === true;
        if (customerIsUser) setAuditNotice(true);
      }

      if (!auditNotice) onSaved();
    } catch (e) {
      // 「疑似重复」不是失败：先转成确认框，别演成一次错误提示 + 列表刷新
      const hint = getSlotDuplicateHint(e);
      if (hint) {
        setSlotDuplicate(hint);
        setSubmitError(null);
        return;
      }
      console.error('保存订单失败:', e);
      const errorInfo = handleApiError(e);
      setSubmitError(errorInfo.message);
      showErrorNotification(errorInfo);
      // 补录闸门拒绝不是「列表过期」：刷的是自己的窗口状态，不是背后的列表
      if ((e as { code?: string } | null)?.code === 'BACKFILL_REQUIRED') {
        void refreshOrderBehavior();
        return;
      }
      // 撞车有两种形状（409 / 2xx + success:false），处理必须一致：见 isWriteConflict
      if (isWriteConflict(e, errorInfo)) {
        // 冲突意味着背后那份列表已经不对了：刷它，但弹窗留着——用户填的内容不能跟着没
        onConflict?.();
        if (mode === 'edit' && initial?.id) {
          get<OrderItem>(`/orders/${initial.id}`)
            .then((data) => setLockToken(data?.updatedAt ?? null))
            .catch(() => { /* 回读失败就维持旧令牌：下一次提交仍会撞同一道闸门 */ });
        }
      }
    } finally {
      setSaving(false);
    }
  };

  /** 「不是同一订单」：带确认标记重发一次，幂等键沿用手上这把（提示那次根本没落库） */
  const resendAsDistinctOrder = async () => {
    setSlotDuplicate(null);
    confirmedDuplicateRef.current = true;
    try {
      await handleSubmit(onSubmit)();
    } finally {
      // 授权只管这一次：之后任何一次提交都得重新经过确认框
      confirmedDuplicateRef.current = false;
    }
  };

  /** 「是同一订单」：不提交，内容留着，让人去订单审核里驳回那张 */
  const keepFormForManualReview = () => {
    setSlotDuplicate(null);
    setSubmitError(
      mode === 'create'
        ? '已保留本次填写内容：请到「订单审核」中核对，驳回重复的那一张即可'
        : '本次修改尚未保存：请放弃这次修改，并在「订单审核」中处理那张重复订单'
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className={`absolute top-0 right-0 h-full w-full sm:w-[540px] bg-white flex flex-col transition-transform duration-300 ease-out ${visible ? 'translate-x-0' : 'translate-x-full'}`}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-teal-500">
              <i className={`${mode === 'create' ? 'ri-add-line' : 'ri-pencil-line'} text-white text-base`} />
            </div>
            <div>
              <p className="text-base font-semibold text-gray-900">{mode === 'create' ? '新建订单' : '编辑订单'}</p>
              {mode === 'edit' && (
                <p className="text-xs text-gray-400 font-mono mt-0.5">{initial?.orderNumber ?? initial?.id.slice(0, 8).toUpperCase()}</p>
              )}
            </div>
          </div>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-7">
            {/* 错误提示 */}
            {submitError && (
              <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 text-sm text-rose-600 flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-error-warning-line" />
                </div>
                {submitError}
              </div>
            )}

            <BasicInfoSection
              control={control}
              errors={errors}
              form={form}
              categories={categories}
              serviceTypes={serviceTypes}
            />

            <TimeSection
              control={control}
              errors={errors}
              form={form}
              orderStartMin={orderStartMin}
              durationHint={durationHint}
              arrivalMaxDateTime={arrivalMaxDateTime}
              dateLocked={mode === 'edit'}
              handleEndDateChange={handleEndDateChange}
              handleEndTimeChange={handleEndTimeChange}
              handleArrivalDateTimeChange={handleArrivalDateTimeChange}
            />

            {/*
              档期即时预告（只读、非阻断）：文案直接用服务端算好的那一句，与提交闸门同源。
              提交时确认框已经弹出同一份信息，这里就不叠加第二条。
            */}
            {slotPreview && !slotDuplicate && (
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-start gap-2">
                <i className="ri-calendar-check-line text-amber-600 text-base mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-amber-800 leading-relaxed break-words">{slotPreview.message}</p>
                  <p className="text-xs text-amber-700 mt-1">
                    仅为提醒，可以继续填写；提交时系统仍会请您确认一次
                  </p>
                </div>
              </div>
            )}

            <CustomerSection
              control={control}
              errors={errors}
              form={form}
              setValue={setValue}
              selectedCustomer={selectedCustomer}
              customerSearch={customerSearch}
              showCustomerDrop={showCustomerDrop}
              filteredCustomers={filteredCustomers}
              setCustomerSearch={setCustomerSearch}
              setShowCustomerDrop={setShowCustomerDrop}
            />

            <ActivitySection
              control={control}
              errors={errors}
              showActivity={showActivity}
              setShowActivity={setShowActivity}
            />

            <ItemsSection
              orderItems={orderItems}
              itemsError={itemsError}
              itemsSubtotalPerDay={itemsSubtotalPerDay}
              itemsTotal={itemsTotal}
              numDays={numDays}
              addItem={addItem}
              removeItem={removeItem}
              setItem={setItem}
            />

            <AmountSection
              control={control}
              errors={errors}
              form={form}
              initial={initial}
              isNettedOrder={isNettedOrder}
              hasValidItems={hasValidItems}
              itemsTotal={itemsTotal}
              numDays={numDays}
              effectiveOrderAmount={effectiveOrderAmount}
              remainBalance={remainBalance}
              statusStyle={statusStyle}
              isPaid={isPaid}
              voucherLabel={voucherLabel}
              paymentVoucherUrl={paymentVoucherUrl}
              setPaymentVoucherUrl={setPaymentVoucherUrl}
              setSubmitError={setSubmitError}
            />

            <NotesSection control={control} />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-white shrink-0 flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={saving || !!itemsLoadError}
            className="px-4 py-2 text-sm bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                保存中...
              </div>
            ) : (
              '保存'
            )}
          </button>
        </div>
      </div>

      {/*
        疑似重复订单：弹层已抽成共享组件，与详情抽屉的改档期/改派客户走同一套确认机制。
        两个出口的文案按模式分流——编辑态说「驳回那张」会让人以为改派已生效。
      */}
      {slotDuplicate && (
        <SlotDuplicateDialog
          message={slotDuplicate.message}
          duplicates={slotDuplicate.duplicates}
          busy={saving}
          keepLabel={mode === 'create' ? '是同一订单，我去审核驳回' : '是同一订单，我放弃这次修改'}
          continueLabel={mode === 'create' ? '不是同一订单，继续提交' : '不是同一订单，继续保存'}
          onKeep={keepFormForManualReview}
          onContinue={resendAsDistinctOrder}
        />
      )}
    </div>
  );
}